import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { LanguageMenu } from "@/components/language-menu";
import { GoLink, fogClass, ghostButton, portalVars } from "@/features/portal/components/portal-ui";
import { COACH_APP_URL } from "@/features/portal/constants/coach-app";
import { BrandMark } from "@/features/site/components/brand-mark";
import { LandingMenu } from "@/features/site/components/landing-menu";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { appConfig } from "@complete-athlete/enums";

/**
 * The chrome of the app people train in, as opposed to the one their coach runs the business
 * from, in the clinic template's hand: a white head card with the navigation and the fog rising
 * from its bottom edge, the content column, and a white footer. The bar carries the app's pages,
 * a way in, and the way over to the coach app for a trainer who landed here.
 */

/**
 * The pages in the bar. Every one is a page of its own, never a jump to a section of the front
 * page: a link has to be shareable and land on what it names.
 */
const pages = () =>
  [
    { to: "/search", label: m.portal_nav_specialists() },
    { to: "/who-its-for", label: m.portal_nav_for_whom() },
    { to: "/features", label: m.portal_nav_features() },
    { to: "/pricing", label: m.portal_nav_pricing() },
    { to: "/blog", label: m.blog_nav() },
    { to: "/help", label: m.help_nav() },
  ] as const;

export const PortalBrand = ({ className }: { className?: string }) => (
  <Link
    to="/"
    className={cn(
      "group flex items-center gap-2.5 text-lg font-semibold tracking-[-0.02em] whitespace-nowrap",
      className,
    )}
  >
    <BrandMark className="transition-transform duration-500 ease-out group-hover:-rotate-12" />
    {appConfig.name}
  </Link>
);

const navLink = "text-muted-foreground transition-colors hover:text-foreground";

/**
 * The card every portal page opens with: the navigation on top and, on the front page, the whole
 * hero under it. Under `lg` the page links move into the menu behind the round button.
 */
export const PortalHead = ({ children, className }: { children?: ReactNode; className?: string }) => (
  <div
    className={cn(
      "relative isolate overflow-hidden rounded-[32px] border border-border bg-background px-5 pt-5 pb-8 md:px-9 md:pt-[26px] md:pb-11",
      fogClass,
      className,
    )}
  >
    <header className="flex items-center gap-4">
      <PortalBrand className="flex-1" />
      <nav className="hidden items-center gap-4 text-sm font-medium whitespace-nowrap lg:flex xl:gap-[26px]">
        {pages().map((link) => (
          <Link key={link.to} to={link.to} className={navLink} activeProps={{ className: "text-foreground" }}>
            {link.label}
          </Link>
        ))}
      </nav>
      <div className="flex flex-1 items-center justify-end gap-2">
        <LanguageMenu style={portalVars} className="max-sm:hidden" />
        <Link to="/login" className={cn(ghostButton, "h-10 px-[18px] text-sm max-sm:hidden")}>
          {m.landing_nav_sign_in()}
        </Link>
        {COACH_APP_URL ? (
          <GoLink to={COACH_APP_URL} external className="max-sm:hidden">
            {m.portal_nav_coach_app()}
          </GoLink>
        ) : null}
        <LandingMenu
          className="lg:hidden"
          style={portalVars}
          brand={<PortalBrand />}
          links={[
            ...pages().map((link) => ({ label: link.label, to: link.to })),
            { label: m.landing_nav_contact(), to: "/contact" },
          ]}
          cta={
            COACH_APP_URL ? (
              <GoLink to={COACH_APP_URL} external>
                {m.portal_nav_coach_app()}
              </GoLink>
            ) : null
          }
        />
      </div>
    </header>
    {children}
  </div>
);

const columnTitle = "mb-3.5 text-[13px] font-medium text-muted-foreground";
const columnLink = "block py-[5px] text-sm font-medium transition-colors hover:text-muted-foreground";

export const PortalFooter = () => (
  <footer className="mt-3 rounded-[32px] border border-border bg-background px-6 pt-8 pb-5 md:px-10 md:pt-11 md:pb-[26px]">
    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
      <div>
        <PortalBrand />
        <p className="mt-3.5 max-w-[280px] text-sm text-muted-foreground">{m.portal_footer_blurb()}</p>
      </div>
      <div>
        <h4 className={columnTitle}>{m.portal_footer_pages()}</h4>
        {[
          ...pages().filter((link) => link.to !== "/help"),
          { to: "/coaches", label: m.portal_nav_coaches() },
          { to: "/how-it-works", label: m.portal_nav_how() },
        ].map((link) => (
          <Link key={link.to} to={link.to} className={columnLink}>
            {link.label}
          </Link>
        ))}
      </div>
      <div>
        <h4 className={columnTitle}>{m.portal_footer_support()}</h4>
        <Link to="/help" className={columnLink}>
          {m.help_title()}
        </Link>
        <Link to="/contact" className={columnLink}>
          {m.landing_nav_contact()}
        </Link>
      </div>
      <div>
        <h4 className={columnTitle}>{m.portal_footer_account()}</h4>
        <Link to="/login" className={columnLink}>
          {m.landing_nav_sign_in()}
        </Link>
        <Link to="/signup" className={columnLink}>
          {m.portal_nav_sign_up()}
        </Link>
      </div>
      {COACH_APP_URL ? (
        <div>
          <h4 className={columnTitle}>{m.portal_footer_coach()}</h4>
          <a href={COACH_APP_URL} className={columnLink}>
            {m.portal_coach_cta()}
          </a>
        </div>
      ) : null}
    </div>
    <div className="mt-11 flex flex-col justify-between gap-1.5 border-t border-border pt-5 text-[13px] text-muted-foreground sm:flex-row">
      <span>
        © {new Date().getFullYear()} {appConfig.name}
      </span>
      <span className="flex flex-wrap gap-x-4 gap-y-1">
        <Link to="/privacy">{m.footer_privacy()}</Link>
        <Link to="/terms">{m.footer_terms()}</Link>
        <Link to="/cookies">{m.footer_cookies()}</Link>
        <Link to="/consumer-health-data">{m.footer_health_data()}</Link>
        <Link to="/legal">{m.footer_legal()}</Link>
      </span>
    </div>
  </footer>
);

/**
 * One portal page: the head card, the content column and the footer. `head` replaces the bare
 * navigation card when the page has a hero to put inside it.
 */
export const PortalPage = ({ head, children }: { head?: ReactNode; children: ReactNode }) => (
  <div style={portalVars} className="min-h-svh bg-page-background p-3 text-sm text-foreground">
    <div className="mx-auto max-w-[1416px]">
      {head ?? <PortalHead />}
      <main>{children}</main>
      <PortalFooter />
    </div>
  </div>
);
